// WhatsNewModal — a centered modal over a dim backdrop, on Soft (the structure
// is the old one's; the look is whatsnew.module.css), with three views:
//   • current  — the current release as a carousel: header (title + version chip
//     + close) · media preview with a feature pill · slide title + body ·
//     pagination dots · footer ("See previous updates" + Back / Next→Done).
//   • previous — scannable changelog: each past release is a clickable row
//     (version + date + chevron, hover-lit) listing its features; footer "Back
//     to what's new".
//   • detail   — a past release opened as the same carousel (its version's
//     digest); footer link "Previous updates" returns to the changelog.
// Closes on the close button, Escape, a backdrop click, or "Done"; locks body
// scroll while open and resets to the current view + slide 0 on open.
//
// Auto-shown once per release: AppLayout opens it when the stored "seen" version
// differs from RELEASE_VERSION and writes the version on close.
//
// Контент — РЕАЛЬНЫЕ релиз-ноты (releases.ts, обновляются вручную с релизом);
// история начинается с v8-редизайна, поэтому чейнджлог скрыт, пока
// PREVIOUS_RELEASES пуст.

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BriefcaseIcon,
  CaretRightIcon,
  ClockCounterClockwiseIcon,
  GiftIcon,
  type Icon,
  LightningIcon,
  PulseIcon,
  SparkleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react'
import soft from '../../styles/soft.module.css'
import { figs } from '../ui/fig'
import {
  CURRENT_RELEASE,
  PREVIOUS_RELEASES,
  type Release,
  type SlideIcon,
  type SlideMedia,
} from './releases'
import styles from './whatsnew.module.css'

interface Props {
  open: boolean
  onClose: () => void
}

type View = 'current' | 'previous' | 'detail'

const PILL_ICON: Record<SlideIcon, Icon> = {
  bolt: LightningIcon,
  pulse: PulseIcon,
  portfolio: BriefcaseIcon,
  gift: GiftIcon,
}

export function WhatsNewModal({ open, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  const downOnBackdrop = useRef(false)
  const [view, setView] = useState<View>('current')
  const [i, setI] = useState(0)
  const [detail, setDetail] = useState(0)

  // Native <dialog> drives modality (focus-trap, top-layer, Escape→onClose); we
  // add body-scroll-lock and reset to the current view + first slide on open.
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open) {
      setView('current')
      setI(0)
      if (!d.open) d.showModal()
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prev
      }
    }
    if (d.open) d.close()
  }, [open])

  const detailRelease = PREVIOUS_RELEASES[detail]
  const openDetail = (idx: number) => {
    setDetail(idx)
    setI(0)
    setView('detail')
  }
  const backToCurrent = () => {
    setI(0)
    setView('current')
  }

  return (
    // Click on the dialog itself = the ::backdrop area → close. Keyboard dismiss
    // is the native <dialog> Escape (fires onClose), so no key handler is needed.
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop pointer-dismiss; keyboard close is native Escape
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-label={view === 'previous' ? 'Previous updates' : "What's New"}
      onClose={onClose}
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === ref.current
      }}
      onClick={(e) => {
        if (e.target === ref.current && downOnBackdrop.current) onClose()
      }}
    >
      {open && (
        <div className={styles.surface}>
          {view === 'current' && (
            <ReleaseCarousel
              release={CURRENT_RELEASE}
              i={i}
              setI={setI}
              onClose={onClose}
              // чейнджлог появляется, когда накопится история релизов
              secondaryLabel={PREVIOUS_RELEASES.length > 0 ? 'See previous updates' : undefined}
              secondaryDir="forward"
              onSecondary={() => setView('previous')}
            />
          )}
          {view === 'previous' && (
            <PreviousUpdates onClose={onClose} onBack={backToCurrent} onOpen={openDetail} />
          )}
          {view === 'detail' && detailRelease && (
            <ReleaseCarousel
              release={detailRelease}
              i={i}
              setI={setI}
              onClose={onClose}
              secondaryLabel="Previous updates"
              secondaryDir="back"
              onSecondary={() => setView('previous')}
            />
          )}
        </div>
      )}
    </dialog>
  )
}

// ---- Release carousel (drives both the current release and a past digest) --
function ReleaseCarousel({
  release,
  i,
  setI,
  onClose,
  secondaryLabel,
  secondaryDir,
  onSecondary,
}: {
  release: Release
  i: number
  setI: Dispatch<SetStateAction<number>>
  onClose: () => void
  secondaryLabel?: string
  secondaryDir: 'back' | 'forward'
  onSecondary: () => void
}) {
  const { slides } = release
  const last = slides.length - 1
  const slide = slides[i] ?? slides[0]
  const PillIcon = PILL_ICON[slide.icon]

  return (
    <>
      <div className={styles.header}>
        <div className={styles.heading}>
          <span className={styles.headMark} aria-hidden="true">
            <SparkleIcon size={18} weight="bold" />
          </span>
          <span className={styles.title}>What's New</span>
          <span className={styles.version}>{figs(release.version)}</span>
        </div>
        <button
          type="button"
          className={`${soft.btn} ${soft.round} ${soft.small} ${styles.close}`}
          onClick={onClose}
          aria-label="Close what's new"
        >
          <XIcon size={16} weight="bold" aria-hidden="true" />
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.media}>
          <div className={styles.preview}>
            <Media kind={slide.media} />
            <span className={styles.pill}>
              <PillIcon size={14} weight="fill" className={styles.pillIcon} aria-hidden="true" />
              {figs(slide.pill)}
            </span>
          </div>
        </div>

        <div className={styles.copy}>
          <span className={styles.slideTitle}>{slide.title}</span>
          <span className={styles.slideBody}>{slide.body}</span>
        </div>

        <div className={styles.dots} aria-label="Slides">
          {slides.map((s, idx) => (
            <button
              key={s.pill}
              type="button"
              className={`${styles.dot} ${idx === i ? styles.dotActive : ''}`}
              aria-label={`Go to ${s.pill}`}
              aria-current={idx === i}
              onClick={() => setI(idx)}
            />
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        {secondaryLabel ? (
          <button type="button" className={styles.prev} onClick={onSecondary}>
            {secondaryDir === 'back' && (
              <ArrowLeftIcon
                size={14}
                weight="bold"
                className={styles.prevIcon}
                aria-hidden="true"
              />
            )}
            {figs(secondaryLabel)}
            {secondaryDir === 'forward' && (
              <CaretRightIcon
                size={12}
                weight="bold"
                className={styles.prevIcon}
                aria-hidden="true"
              />
            )}
          </button>
        ) : (
          <span />
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={`${soft.btn} ${styles.back}`}
            disabled={i === 0}
            onClick={() => setI((n) => Math.max(0, n - 1))}
          >
            {figs('Back')}
          </button>
          <button
            type="button"
            className={`${soft.action} ${styles.next}`}
            onClick={() => (i === last ? onClose() : setI((n) => Math.min(last, n + 1)))}
          >
            {figs(i === last ? 'Done' : 'Next')}
            {i !== last && <ArrowRightIcon size={14} weight="bold" aria-hidden="true" />}
          </button>
        </div>
      </div>
    </>
  )
}

// ---- Previous updates changelog (each release row opens its digest) --------
function PreviousUpdates({
  onClose,
  onBack,
  onOpen,
}: {
  onClose: () => void
  onBack: () => void
  onOpen: (idx: number) => void
}) {
  return (
    <>
      <div className={styles.header}>
        <div className={styles.heading}>
          <span className={styles.headMark} aria-hidden="true">
            <ClockCounterClockwiseIcon size={18} weight="bold" />
          </span>
          <span className={styles.title}>Previous updates</span>
        </div>
        <button
          type="button"
          className={`${soft.btn} ${soft.round} ${soft.small} ${styles.close}`}
          onClick={onClose}
          aria-label="Close previous updates"
        >
          <XIcon size={16} weight="bold" aria-hidden="true" />
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.log}>
          {PREVIOUS_RELEASES.map((rel, idx) => (
            <button
              key={rel.version}
              type="button"
              className={styles.release}
              onClick={() => onOpen(idx)}
              aria-label={`What's new in v${rel.version}`}
            >
              <span className={styles.releaseHead}>
                <span className={styles.releaseTag}>
                  <span className={styles.releaseDot} />
                  <span className={styles.releaseVer}>v{rel.version}</span>
                </span>
                <span className={styles.releaseRight}>
                  <span className={styles.releaseDate}>{rel.date}</span>
                  <CaretRightIcon
                    size={13}
                    weight="bold"
                    className={styles.releaseChevron}
                    aria-hidden="true"
                  />
                </span>
              </span>
              {rel.slides.map((e) => (
                <span key={e.title} className={styles.entry}>
                  <Thumb kind={e.media} />
                  <span className={styles.entryText}>
                    <span className={styles.entryTitle}>{e.title}</span>
                    <span className={styles.entryBody}>{e.body}</span>
                  </span>
                </span>
              ))}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={`${soft.action} ${styles.next} ${styles.backFull}`}
          onClick={onBack}
        >
          <ArrowLeftIcon size={14} weight="bold" aria-hidden="true" />
          {figs("Back to what's new")}
        </button>
      </div>
    </>
  )
}

// Built-in media illustrations for the carousel preview. The chart is the exact
// artboard motif; the rest are on-brand sample motifs. All use a 522×236 viewBox
// (≈ the preview frame) so strokes/rects read at design scale.
function Media({ kind }: { kind: SlideMedia }) {
  switch (kind) {
    case 'chart':
      return (
        <svg
          className={styles.illu}
          viewBox="0 0 522 236"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M0,78 L522,78" fill="none" stroke="var(--chart-grid)" />
          <path d="M0,158 L522,158" fill="none" stroke="var(--chart-grid)" />
          <path
            d="M0,176 L44,162 L88,168 L132,146 L176,154 L220,126 L264,134 L308,106 L352,116 L396,82 L440,94 L484,58 L522,66 L522,236 L0,236 Z"
            fill="var(--accent-weak)"
          />
          <path
            d="M0,176 L44,162 L88,168 L132,146 L176,154 L220,126 L264,134 L308,106 L352,116 L396,82 L440,94 L484,58 L522,66"
            fill="none"
            stroke="var(--accent)"
            strokeWidth="2"
          />
          <circle cx="484" cy="58" r="8" fill="var(--accent-weak)" />
          <circle cx="484" cy="58" r="3.5" fill="var(--accent)" />
        </svg>
      )
    case 'columns':
      return (
        <svg
          className={styles.illu}
          viewBox="0 0 522 236"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {[40, 199, 358].map((x, col) =>
            [28, 86, 144].map((y, row) => {
              const accent = col === 1 && row === 0
              return (
                <rect
                  key={`${x}-${y}`}
                  x={x}
                  y={y}
                  width="124"
                  height="46"
                  rx="8"
                  fill="var(--surface-2)"
                  stroke={accent ? 'var(--accent-line)' : 'var(--line-soft)'}
                />
              )
            })
          )}
          <rect x="52" y="40" width="28" height="6" rx="3" fill="var(--accent)" />
        </svg>
      )
    case 'bars':
      return (
        <svg
          className={styles.illu}
          viewBox="0 0 522 236"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M40,190 L482,190" fill="none" stroke="var(--chart-grid)" />
          {[56, 84, 70, 104, 92, 128].map((h, idx) => {
            const x = 57 + idx * 72
            const peak = idx === 5
            return (
              <rect
                key={x}
                x={x}
                y={190 - h}
                width="48"
                height={h}
                rx="6"
                fill={peak ? 'var(--accent)' : 'var(--surface-2)'}
                stroke={peak ? 'none' : 'var(--line-soft)'}
              />
            )
          })}
        </svg>
      )
    default:
      return (
        <svg
          className={styles.illu}
          viewBox="0 0 522 236"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="261" cy="118" r="64" fill="var(--accent-weak)" />
          <circle cx="200" cy="70" r="3" fill="var(--accent)" />
          <circle cx="330" cy="162" r="3.5" fill="var(--accent)" />
          <path
            d="M261,76 L271,104.25 L300.95,105.02 L277.17,123.25 L285.69,151.98 L261,135 L236.31,151.98 L244.83,123.25 L221.05,105.02 L251.01,104.25 Z"
            fill="var(--accent)"
          />
        </svg>
      )
  }
}

// Small 64×40 changelog thumbnails (1:1 viewBox → no distortion).
function Thumb({ kind }: { kind: SlideMedia }) {
  return (
    <span className={styles.thumb}>
      <svg
        className={styles.thumbIllu}
        viewBox="0 0 64 40"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {kind === 'chart' && (
          <>
            <path
              d="M0,30 L16,24 L28,27 L40,16 L52,19 L64,8 L64,40 L0,40 Z"
              fill="var(--accent-weak)"
            />
            <path
              d="M0,30 L16,24 L28,27 L40,16 L52,19 L64,8"
              fill="none"
              stroke="var(--accent)"
              strokeWidth="2"
            />
          </>
        )}
        {kind === 'bars' && (
          <>
            <rect x="8" y="22" width="8" height="18" rx="2" fill="var(--surface-2)" />
            <rect x="20" y="14" width="8" height="26" rx="2" fill="var(--surface-2)" />
            <rect x="32" y="18" width="8" height="22" rx="2" fill="var(--surface-2)" />
            <rect x="44" y="8" width="8" height="32" rx="2" fill="var(--accent)" />
          </>
        )}
        {kind === 'columns' && (
          <>
            <rect
              x="6"
              y="8"
              width="16"
              height="24"
              rx="3"
              fill="var(--surface-2)"
              stroke="var(--accent-line)"
            />
            <rect x="24" y="8" width="16" height="24" rx="3" fill="var(--surface-2)" />
            <rect x="42" y="8" width="16" height="24" rx="3" fill="var(--surface-2)" />
          </>
        )}
        {kind === 'rewards' && (
          <>
            <circle cx="32" cy="20" r="14" fill="var(--accent-weak)" />
            <path
              d="M32,8 L35,17 L44,17 L37,23 L39,32 L32,26 L25,32 L27,23 L20,17 L29,17 Z"
              fill="var(--accent)"
            />
          </>
        )}
      </svg>
    </span>
  )
}
